/**
 * Badge Manager
 * Handles badge unlocking and checking
 */

import { doc, updateDoc, arrayUnion, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getAllBadges } from '../data/badgeDefinitions';

/**
 * Check if user meets badge requirement
 * @param {object} badge - Badge definition
 * @param {object} userStats - User stats from Firestore
 * @returns {boolean} - True if requirement is met
 */
export const checkBadgeRequirement = (badge, userStats) => {
  const { requirement } = badge;
  const stats = userStats.stats || {};

  switch (requirement.type) {
    case 'shifts_completed':
      return (stats.totalShifts || 0) >= requirement.value;
    
    case 'inspections_completed':
      return (stats.totalInspections || 0) >= requirement.value;
    
    case 'borrows_completed':
      return (stats.totalBorrows || 0) >= requirement.value;
    
    case 'repairs_completed':
      return (stats.totalRepairs || 0) >= requirement.value;
    
    case 'perfect_attendance_days':
      return (stats.perfectAttendanceDays || 0) >= requirement.value;
    
    case 'night_shifts_completed':
      return (stats.nightShiftsCompleted || 0) >= requirement.value;
    
    case 'stock_transactions':
      return (stats.stockTransactions || 0) >= requirement.value;
    
    case 'teams_joined':
      return (userStats.teamsJoined || 0) >= requirement.value;
    
    case 'teams_led':
      return (userStats.teamsLed || 0) >= requirement.value;
    
    case 'teams_created':
      return (userStats.teamsCreated || 0) >= requirement.value;
    
    case 'gifts_given':
      return (userStats.giftsGiven || 0) >= requirement.value;
    
    case 'gifts_received':
      return (userStats.giftsReceived || 0) >= requirement.value;
    
    case 'gifts_total_value':
      return (userStats.giftsTotalValue || 0) >= requirement.value;
    
    case 'level':
      return (userStats.level || 1) >= requirement.value;
    
    default:
      return false;
  }
};

/**
 * Check and unlock badges for a user
 * @param {string} userId - User ID
 * @param {object} userStats - User stats
 * @param {function} addTCoins - Function to add T Coins
 * @returns {Promise<Array>} - Array of unlocked badge IDs
 */
export const checkAndUnlockBadges = async (userId, userStats, addTCoins = null) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return [];
    }

    const userData = userSnap.data();
    const currentBadges = userData.badges || [];
    const allBadges = getAllBadges();
    const unlockedBadges = [];

    // Check each badge
    for (const badge of allBadges) {
      // Skip if already unlocked
      if (currentBadges.includes(badge.badgeId)) {
        continue;
      }

      // Check requirement
      const meetsRequirement = checkBadgeRequirement(badge, {
        ...userData,
        stats: userData.stats || {}
      });

      if (meetsRequirement) {
        // Unlock badge
        await updateDoc(userRef, {
          badges: arrayUnion(badge.badgeId),
          updatedAt: serverTimestamp()
        });

        // Award badge rewards
        if (badge.reward.points > 0 || badge.reward.xp > 0 || badge.reward.coins > 0) {
          const currentPoints = userData.totalPoints || 0;
          const currentXP = userData.currentXP || 0;
          const totalXP = userData.totalXP || 0;

          await updateDoc(userRef, {
            totalPoints: currentPoints + badge.reward.points,
            currentXP: currentXP + badge.reward.xp,
            totalXP: totalXP + badge.reward.xp,
            updatedAt: serverTimestamp()
          });

          // Add coins if provided
          if (badge.reward.coins > 0 && addTCoins) {
            await addTCoins(userId, 'virtual', badge.reward.coins, `รางวัล Badge: ${badge.nameThai}`);
          }
        }

        unlockedBadges.push(badge.badgeId);
      }
    }

    return unlockedBadges;
  } catch (error) {
    console.error('Error checking and unlocking badges:', error);
    return [];
  }
};

/**
 * Get user's unlocked badges
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of badge IDs
 */
export const getUserBadges = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return [];
    }

    const userData = userSnap.data();
    return userData.badges || [];
  } catch (error) {
    console.error('Error getting user badges:', error);
    return [];
  }
};

